// import library
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
// import component
import Layout from "../../../layouts/Layout";
import LoaderIcon from "../../../components/Common/Icon/Loader";
import { env } from "../../../env";
import { AuthContext } from "../../../context";
// import child components (untouched originals)
import ChatSidebar from "./ChatSidebar";
import ChatTopBar from "./ChatTopBar";
import MessageList from "./MessageList";
import ChatComposer from "./ChatComposer";
import InfoPanel from "./InfoPanel";
import DeleteConfirmModal from "./DeleteConfirmModal";
import FeedbackModal from "./FeedbackModal";

const SESSION_PAGE_SIZE = 10;
const PICKER_PAGE_SIZE = 10;
const FALLBACK_SALESPERSON_ID = "70";
const CUSTOMER_STORAGE_KEY = "ai_chat_last_customer";
const CHIP_CLASSES = [
    "from-sky-200/90 to-indigo-200/70 text-indigo-900",
    "from-pink-200/90 to-fuchsia-200/70 text-fuchsia-900",
    "from-amber-200/90 to-orange-200/70 text-amber-950",
    "from-emerald-200/90 to-teal-200/70 text-emerald-950",
];

const nowIso = () => new Date().toISOString();
const shortTitle = (text) =>
    text.length > 25 ? `${text.slice(0, 25).trim()}...` : text;

const formatTimestamp = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDayLabel = (isoString) => {
    if (!isoString) return "Today";
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";
    return date.toLocaleDateString();
};

const AiChatPage = () => {
    const { t } = useTranslation();
    const { aiChatBaseUrl, aiChatAuth } = useContext(AuthContext);

    // ─── Selected customer (stored in localStorage) ───────────────────────────
    const [selectedCustomer, setSelectedCustomer] = useState(() => {
        try {
            const saved = localStorage.getItem(CUSTOMER_STORAGE_KEY);
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });

    // ─── Customer picker dropdown ──────────────────────────────────────────────
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerSearch, setPickerSearch] = useState("");
    const [pickerCustomers, setPickerCustomers] = useState([]);
    const [pickerPage, setPickerPage] = useState(1);
    const [pickerHasNext, setPickerHasNext] = useState(false);
    const [pickerLoading, setPickerLoading] = useState(false);
    const pickerRef = useRef(null);
    const pickerDebounceRef = useRef(null);

    // ─── No-customer warning ───────────────────────────────────────────────────
    const [showNoCustomerWarning, setShowNoCustomerWarning] = useState(false);
    const warningTimerRef = useRef(null);

    // ─── Customer API data ─────────────────────────────────────────────────────
    const [customerData, setCustomerData] = useState({});
    const [customerCode, setCustomerCode] = useState("");
    const [salespersonId, setSalespersonId] = useState("");
    const [isCustomerLoading, setIsCustomerLoading] = useState(false);

    // ─── Chat UI state ─────────────────────────────────────────────────────────
    const [query, setQuery] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [viewDensity, setViewDensity] = useState("comfortable");
    const [showInfoPanel, setShowInfoPanel] = useState(true);
    const [isLoadingChats, setIsLoadingChats] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [sessionPage, setSessionPage] = useState(1);
    const [sessionHasNext, setSessionHasNext] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(320);
    const [isResizing, setIsResizing] = useState(false);
    const [isLarge, setIsLarge] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth >= 1024 : true
    );

    const [draft, setDraft] = useState("");
    const [sessionId, setSessionId] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [thinkingStage, setThinkingStage] = useState("");

    const [renamingChatId, setRenamingChatId] = useState("");
    const [renameValue, setRenameValue] = useState("");
    const [copyNoticeId, setCopyNoticeId] = useState("");
    const [menuChatId, setMenuChatId] = useState("");
    const [confirmDeleteId, setConfirmDeleteId] = useState("");
    const [feedbackTarget, setFeedbackTarget] = useState(null);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [chips, setChips] = useState([]);

    const [chats, setChats] = useState([]);
    const [activeChatId, setActiveChatId] = useState("");

    const containerRef = useRef(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    const activeChat = useMemo(
        () => chats.find((chat) => chat.id === activeChatId) || chats[0],
        [activeChatId, chats]
    );
    const pinnedChats = useMemo(() => chats.filter((c) => c.pinned), [chats]);
    const visiblePinned = useMemo(() => pinnedChats, [pinnedChats]);
    const recentChats = useMemo(() => chats.filter((c) => !c.pinned), [chats]);

    const groupedMessages = useMemo(() => {
        if (!activeChat?.messages?.length) return [];
        const sorted = [...activeChat.messages].sort((a, b) =>
            (a.createdAt || "").localeCompare(b.createdAt || "")
        );
        const result = [];
        let lastLabel = "";
        sorted.forEach((msg) => {
            const label = formatDayLabel(msg.createdAt);
            if (label !== lastLabel) {
                result.push({ type: "separator", label });
                lastLabel = label;
            }
            result.push({ type: "message", message: msg });
        });
        return result;
    }, [activeChat]);

    // ─── Picker: close on outside click ───────────────────────────────────────
    useEffect(() => {
        if (!pickerOpen) return;
        const handle = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [pickerOpen]);

    // ─── Picker: fetch customers ───────────────────────────────────────────────
    const fetchPickerCustomers = async (searchTerm, pageNum, append = false) => {
        const baseUrl = env.REACT_APP_API_URL;
        const token = localStorage.getItem("token");
        setPickerLoading(true);
        try {
            const response = await axios.post(
                `${baseUrl}/api/Customer/GetCustomerDetailSearch`,
                {
                    page_num: pageNum,
                    page_size: PICKER_PAGE_SIZE,
                    search_term: searchTerm,
                    sort_by: 'c."Name"',
                    sort_order: "asc",
                    month_info: "",
                    state_info: "",
                    city_info: "",
                    payment_info: "",
                    customer_type_id: "",
                    prospect_customer: "",
                },
                {
                    headers: {
                        Accept: "Application/json",
                        "Content-Type": "Application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const data = response.data;
            const list = Array.isArray(data?.list) ? data.list : [];
            const totalPages = data?.totalpages || 1;
            setPickerCustomers((prev) => (append ? [...prev, ...list] : list));
            setPickerPage(pageNum);
            setPickerHasNext(pageNum < totalPages);
        } catch (error) {
            if (
                error.response &&
                (error.response.status === 401 || error.response.status === 404)
            ) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        } finally {
            setPickerLoading(false);
        }
    };

    const openPicker = () => {
        setPickerOpen(true);
        setPickerSearch("");
        setPickerCustomers([]);
        setPickerPage(1);
        fetchPickerCustomers("", 1, false);
    };

    const handlePickerSearch = (e) => {
        const val = e.target.value;
        setPickerSearch(val);
        clearTimeout(pickerDebounceRef.current);
        pickerDebounceRef.current = setTimeout(() => {
            setPickerCustomers([]);
            setPickerPage(1);
            fetchPickerCustomers(val, 1, false);
        }, 400);
    };

    // ─── Customer selection ────────────────────────────────────────────────────
    const applyCustomer = (customer) => {
        setPickerOpen(false);
        setSelectedCustomer(customer);
        localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(customer));
        loadCustomerAndSessions(customer.id);
    };

    const loadCustomerAndSessions = async (custId) => {
        const baseUrl = env.REACT_APP_API_URL;
        const token = localStorage.getItem("token");

        setIsCustomerLoading(true);
        setChats([]);
        setActiveChatId("");
        setSessionId("");
        setSessionPage(1);
        setSessionHasNext(false);
        setCustomerCode("");
        setCustomerData({});
        setSalespersonId("");

        try {
            const response = await axios.post(
                `${baseUrl}/api/Customer/GetCustomerDetail`,
                { customerid: custId },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const customer = response?.data?.length ? response.data[0] : {};
            setCustomerData(customer);
            const spId =
                customer?.salesperson_id ||
                customer?.SalespersonId ||
                customer?.salespersonId ||
                customer?.salesPersonId ||
                "";
            const code = customer?.code || "";
            const nextCode = code || String(custId);
            setCustomerCode(nextCode);
            if (spId) setSalespersonId(spId);
            fetchSessions(1, false, nextCode, spId || FALLBACK_SALESPERSON_ID, query, activeFilter);
        } catch (error) {
            if (
                error.response &&
                (error.response.status === 401 || error.response.status === 404)
            ) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        } finally {
            setIsCustomerLoading(false);
        }
    };

    // ─── Init once aiChatBaseUrl is available from context ───────────────────
    const initDoneRef = useRef(false);
    useEffect(() => {
        if (!aiChatBaseUrl || initDoneRef.current) return;
        initDoneRef.current = true;
        if (selectedCustomer?.id) loadCustomerAndSessions(selectedCustomer.id);
        fetchStarterQuestions();
    }, [aiChatBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Re-fetch sessions when search / filter changes ────────────────────────
    useEffect(() => {
        if (!customerCode) return;
        const timer = setTimeout(() => {
            setChats([]);
            setActiveChatId("");
            setSessionId("");
            setSessionPage(1);
            setSessionHasNext(false);
            fetchSessions(1, false, customerCode, salespersonId || FALLBACK_SALESPERSON_ID, query, activeFilter);
        }, 400);
        return () => clearTimeout(timer);
    }, [query, activeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Resize observer ──────────────────────────────────────────────────────
    useEffect(() => {
        const handleResize = () => setIsLarge(window.innerWidth >= 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchStarterQuestions = async () => {
        try {
            const response = await fetch(`${aiChatBaseUrl}/starter-questions`, {
                method: "GET",
                headers: { Authorization: aiChatAuth },
            });
            if (!response.ok) throw new Error("Starter questions request failed");
            const payload = await response.json();
            const list = Array.isArray(payload?.data) ? payload.data : [];
            const mapped = list
                .filter((item) => item?.is_active !== false && item?.description)
                .map((item, index) => ({
                    label: item.title?.trim() || `Suggestion ${index + 1}`,
                    cls: CHIP_CLASSES[index % CHIP_CLASSES.length],
                    question: item.description.trim(),
                }));
            setChips(mapped);
        } catch {
            // leave chips empty on error
        }
    };

    // ─── Load history when chat selected ─────────────────────────────────────
    useEffect(() => {
        if (!activeChatId) return;
        const current = chats.find((chat) => chat.id === activeChatId);
        if (current && !current.isLocal && !current.historyLoaded) {
            loadHistory(activeChatId);
            setSessionId(activeChatId);
        }
    }, [activeChatId, chats]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Keyboard shortcuts ───────────────────────────────────────────────────
    useEffect(() => {
        const handleKey = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                handleNewChat();
            }
            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                event.preventDefault();
                handleSend();
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    });

    // ─── Sidebar resize drag ──────────────────────────────────────────────────
    useEffect(() => {
        const handleMove = (event) => {
            if (!isResizing) return;
            const container = containerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            const nextWidth = Math.min(420, Math.max(260, event.clientX - rect.left));
            setSidebarWidth(nextWidth);
        };
        const handleUp = () => setIsResizing(false);
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, [isResizing]);

    // ─── API: fetch sessions ──────────────────────────────────────────────────
    const fetchSessions = async (
        page = 1,
        append = false,
        code = "",
        spId = "",
        searchTerm = "",
        timeFilter = ""
    ) => {
        setIsLoadingChats(true);
        try {
            const params = new URLSearchParams({
                salesperson_id: spId || salespersonId || FALLBACK_SALESPERSON_ID,
                customer_id: code || customerCode,
                page,
                page_size: SESSION_PAGE_SIZE,
                search_term: searchTerm,
            });
            if (timeFilter && timeFilter !== "all") params.set("time_filter", timeFilter);
            const response = await fetch(`${aiChatBaseUrl}/sessions?${params.toString()}`, {
                method: "GET",
                headers: { Authorization: aiChatAuth },
            });
            if (!response.ok) throw new Error("Session list request failed");

            const data = await response.json();
            const items = Array.isArray(data?.data) ? data.data : [];
            const mapped = items.map((item) => ({
                id: item.session_id,
                title:
                    item.display_title?.trim()
                        ? item.display_title
                        : item.salesperson_first_question?.trim()
                        ? shortTitle(item.salesperson_first_question)
                        : t('aura default session title'),
                updatedAt: formatTimestamp(item.last_chat_time),
                isLocal: false,
                pinned: Boolean(item.is_pinned),
                unread: 0,
                messages: [],
                totalMessages: item.total_messages,
                sessionCreatedAt: item.session_created_at,
                lastChatTime: item.last_chat_time,
                historyLoaded: false,
            }));

            setChats((prev) => (append ? [...prev, ...mapped] : mapped));
            setSessionPage(page);
            setSessionHasNext(Boolean(data?.pagination?.has_next));
            if (mapped.length) setActiveChatId((prev) => prev || mapped[0].id);

            // Auto-start a new conversation when the customer has no sessions yet
            if (!append && mapped.length === 0) {
                const newChat = {
                    id: `local-${Date.now()}`,
                    title: t('aura new chat'),
                    updatedAt: t('aura topbar just now'),
                    isLocal: true,
                    historyLoaded: true,
                    pinned: false,
                    unread: 0,
                    messages: [
                        {
                            id: `m-${Date.now()}`,
                            role: "assistant",
                            content: t('aura default greeting'),
                            createdAt: nowIso(),
                            reaction: "",
                            saved: false,
                        },
                    ],
                };
                setChats([newChat]);
                setActiveChatId(newChat.id);
                setSessionId("");
            }
        } catch {
            setSessionHasNext(false);
        } finally {
            setIsLoadingChats(false);
        }
    };

    // ─── API: load message history ────────────────────────────────────────────
    const loadHistory = async (session) => {
        if (!session) return;
        setIsLoadingHistory(true);
        try {
            const response = await fetch(
                `${aiChatBaseUrl}/sessions/${session}/chats?page=1&page_size=50`,
                { method: "GET", headers: { Authorization: aiChatAuth } }
            );
            if (!response.ok) throw new Error("Chat history request failed");

            const data = await response.json();
            const rawMessages = Array.isArray(data?.data?.messages)
                ? data.data.messages
                : Array.isArray(data?.data)
                ? data.data
                : [];

            const normalized = rawMessages
                .map((item) => item?.json ?? item)
                .filter(Boolean)
                .map((entry) => {
                    const feedback = entry.feedback || null;
                    const content = entry.content || "";
                    return {
                        id: entry.id ? `msg-${entry.id}` : `msg-${entry.created_at || Math.random()}`,
                        role: entry.role,
                        content,
                        createdAt: entry.created_at || nowIso(),
                        reaction: feedback
                            ? (feedback.is_positive ? "up" : "down")
                            : "",
                        feedback: feedback,
                        saved: false,
                    };
                })
                .sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));

            setChats((prev) =>
                prev.map((chat) => {
                    if (chat.id !== session) return chat;
                    return {
                        ...chat,
                        messages: normalized,
                        historyLoaded: true,
                    };
                })
            );
            scrollToBottom();
        } catch {
            // ignore
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // ─── Handlers ─────────────────────────────────────────────────────────────
    const handleNewChat = () => {
        const newChat = {
            id: `local-${Date.now()}`,
            title: t('aura new chat'),
            updatedAt: t('aura topbar just now'),
            isLocal: true,
            historyLoaded: true,
            pinned: false,
            unread: 0,
            messages: [
                {
                    id: `m-${Date.now()}`,
                    role: "assistant",
                    content: t('aura default greeting'),
                    createdAt: nowIso(),
                    reaction: "",
                    saved: false,
                },
            ],
        };
        setChats((prev) => [newChat, ...prev]);
        setActiveChatId(newChat.id);
        setSessionId("");
    };

    const handleSend = async (questionText = null) => {
        if (!selectedCustomer) {
            clearTimeout(warningTimerRef.current);
            setShowNoCustomerWarning(true);
            warningTimerRef.current = setTimeout(() => setShowNoCustomerWarning(false), 4000);
            return;
        }

        const trimmed = (typeof questionText === "string" && questionText) ? questionText.trim() : draft.trim();
        if (!trimmed || isSending) return;

        setIsSending(true);
        const capturedChatId = activeChatId;
        const streamingMsgId = `streaming-${Date.now()}`;
        const safeSessionId =
            sessionId && !String(sessionId).startsWith("local-")
                ? sessionId
                : undefined;

        setChats((prev) =>
            prev.map((chat) => {
                if (chat.id !== capturedChatId) return chat;
                return {
                    ...chat,
                    title: chat.title === t('aura new chat') ? shortTitle(trimmed) : chat.title,
                    updatedAt: t('aura topbar just now'),
                    messages: [
                        ...chat.messages,
                        { id: `m-${Date.now()}`, role: "user", content: trimmed, createdAt: nowIso() },
                    ],
                };
            })
        );
        if (!(typeof questionText === "string" && questionText)) setDraft("");
        scrollToBottom();

        try {
            const response = await fetch(`${aiChatBaseUrl}/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: aiChatAuth },
                body: JSON.stringify({
                    salesperson_id: salespersonId || FALLBACK_SALESPERSON_ID,
                    customer_id: customerCode || String(selectedCustomer.id),
                    query: trimmed,
                    session_id: safeSessionId,
                }),
            });
            if (!response.ok) throw new Error("Stream request failed");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let streamingStarted = false;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() ?? "";

                for (const block of parts) {
                    const lines = block.split("\n");
                    let eventType = "";
                    let dataStr = "";
                    for (const line of lines) {
                        if (line.startsWith("event: ")) eventType = line.slice(7).trim();
                        else if (line.startsWith("data: ")) dataStr = line.slice(6);
                    }
                    if (!dataStr) continue;

                    let parsed;
                    try { parsed = JSON.parse(dataStr); } catch { continue; }

                    if (eventType === "stage") {
                        setThinkingStage(parsed.name || "");
                        if (parsed.name === "session_ready" && parsed.session_id) {
                            setSessionId(parsed.session_id);
                        }
                    } else if (eventType === "delta") {
                        const token = parsed.content || "";
                        if (!token) continue;

                        if (!streamingStarted) {
                            streamingStarted = true;
                            setChats((prev) =>
                                prev.map((chat) => {
                                    if (chat.id !== capturedChatId) return chat;
                                    return {
                                        ...chat,
                                        messages: [
                                            ...chat.messages,
                                            {
                                                id: streamingMsgId,
                                                role: "assistant",
                                                content: token,
                                                createdAt: nowIso(),
                                                reaction: "",
                                                saved: false,
                                                streaming: true,
                                            },
                                        ],
                                    };
                                })
                            );
                        } else {
                            setChats((prev) =>
                                prev.map((chat) => {
                                    if (chat.id !== capturedChatId) return chat;
                                    return {
                                        ...chat,
                                        messages: chat.messages.map((msg) =>
                                            msg.id === streamingMsgId
                                                ? { ...msg, content: msg.content + token }
                                                : msg
                                        ),
                                    };
                                })
                            );
                        }
                        scrollToBottom();
                    } else if (eventType === "final") {
                        const newSessId = parsed.session_id || safeSessionId || "";
                        const finalContent = parsed.content || "";
                        const finalMsgId = parsed.chat_id ? `msg-${parsed.chat_id}` : streamingMsgId;

                        setChats((prev) =>
                            prev.map((chat) => {
                                if (chat.id !== capturedChatId) return chat;
                                return {
                                    ...chat,
                                    id: chat.isLocal && newSessId ? newSessId : chat.id,
                                    isLocal: false,
                                    historyLoaded: true,
                                    updatedAt: t('aura topbar just now'),
                                    messages: chat.messages.map((msg) =>
                                        msg.id === streamingMsgId
                                            ? { ...msg, id: finalMsgId, content: finalContent, streaming: false }
                                            : msg
                                    ),
                                };
                            })
                        );
                        if (newSessId) {
                            setSessionId(newSessId);
                            localStorage.setItem("aura_last_session_id", newSessId);
                            if (capturedChatId.startsWith("local-")) setActiveChatId(newSessId);
                        }
                        scrollToBottom();
                    }
                }
            }

            // Edge case: stream ended without final event — clear streaming flag
            if (streamingStarted) {
                setChats((prev) =>
                    prev.map((chat) => {
                        if (chat.id !== capturedChatId) return chat;
                        return {
                            ...chat,
                            messages: chat.messages.map((msg) =>
                                msg.id === streamingMsgId ? { ...msg, streaming: false } : msg
                            ),
                        };
                    })
                );
            }
        } catch {
            setChats((prev) =>
                prev.map((chat) => {
                    if (chat.id !== capturedChatId) return chat;
                    return {
                        ...chat,
                        updatedAt: t('aura topbar just now'),
                        messages: [
                            ...chat.messages.filter((m) => m.id !== streamingMsgId),
                            {
                                id: `m-${Date.now()}`,
                                role: "assistant",
                                content: t('aura request failed'),
                                createdAt: nowIso(),
                                reaction: "",
                                saved: false,
                            },
                        ],
                    };
                })
            );
        } finally {
            setIsSending(false);
            setThinkingStage("");
        }
    };

    const handleChipClick = (question) => {
        handleSend(question);
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            // ignore
        }
    };

    const updateMessage = (messageId, updates) => {
        setChats((prev) =>
            prev.map((chat) => {
                if (chat.id !== activeChatId) return chat;
                return {
                    ...chat,
                    messages: chat.messages.map((msg) =>
                        msg.id === messageId ? { ...msg, ...updates } : msg
                    ),
                };
            })
        );
    };
    const startEditMessage = (message) => {
        setDraft(message.content || "");
    };

    const handleReact = (message, reaction) => {
        const resolvedSessionId = activeChatId.startsWith("local-") ? "" : activeChatId;
        setFeedbackTarget({
            messageId: message.id,
            chatId: message.id,
            sessionId: resolvedSessionId,
            reaction,
            existingFeedback: message.feedback || null,
        });
    };

    const handleFeedbackSubmitted = (messageId, reaction) => {
        updateMessage(messageId, { reaction });
        setFeedbackTarget(null);
    };

    const togglePin = async (chatId) => {
        const chat = chats.find((c) => c.id === chatId);
        if (!chat) return;
        const newPinned = !chat.pinned;
        setChats((prev) =>
            prev.map((c) => (c.id === chatId ? { ...c, pinned: newPinned } : c))
        );
        try {
            await fetch(`${aiChatBaseUrl}/sessions/${chatId}/pin`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: aiChatAuth },
                body: JSON.stringify({ is_pinned: newPinned }),
            });
        } catch {
            setChats((prev) =>
                prev.map((c) => (c.id === chatId ? { ...c, pinned: !newPinned } : c))
            );
        }
    };

    const deleteChat = async (chatId) => {
        try {
            await fetch(`${aiChatBaseUrl}/sessions/${chatId}`, {
                method: "DELETE",
                headers: { Authorization: aiChatAuth },
            });
        } catch {
            // proceed with local removal
        }
        setChats((prev) => prev.filter((chat) => chat.id !== chatId));
        if (activeChatId === chatId) {
            setActiveChatId("");
            setSessionId("");
        }
    };

    const handleRenameStart = () => {
        if (!activeChat) return;
        setRenamingChatId(activeChat.id);
        setRenameValue(activeChat.title);
    };

    const handleRenameCommit = async () => {
        if (!renamingChatId) return;
        const newTitle = renameValue.trim();
        if (!newTitle) { setRenamingChatId(""); setRenameValue(""); return; }
        const oldTitle = chats.find((c) => c.id === renamingChatId)?.title;
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === renamingChatId ? { ...chat, title: newTitle } : chat
            )
        );
        setRenamingChatId("");
        setRenameValue("");
        try {
            await fetch(`${aiChatBaseUrl}/sessions/${renamingChatId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json", Authorization: aiChatAuth },
                body: JSON.stringify({ title: newTitle }),
            });
        } catch {
            if (oldTitle) {
                setChats((prev) =>
                    prev.map((chat) =>
                        chat.id === renamingChatId ? { ...chat, title: oldTitle } : chat
                    )
                );
            }
        }
    };

    const handleSelectChat = (chat) => {
        setActiveChatId(chat.id);
        setSessionId(chat.isLocal ? "" : chat.id);
        if (!chat.isLocal) localStorage.setItem("aura_last_session_id", chat.id);
        if (!chat.messages || chat.messages.length === 0) {
            setIsLoadingHistory(true);
        }
        if (!isLarge) setMobileSidebarOpen(false);
    };

    // ─── Derived display values ───────────────────────────────────────────────
    const customerDisplay =
        customerData?.name || customerData?.code || customerCode || selectedCustomer?.name || "";
    const salespersonDisplay =
        customerData?.salesperson_id ||
        customerData?.SalespersonId ||
        customerData?.salespersonId ||
        customerData?.salesPersonId ||
        salespersonId ||
        FALLBACK_SALESPERSON_ID;

    const isChatsLoading = isCustomerLoading || (isLoadingChats && chats.length === 0);
    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Layout>
            <div
                ref={containerRef}
                className="flex flex-col h-[100dvh] overflow-hidden p-4
                bg-[radial-gradient(circle_at_50%_12%,rgba(255,255,255,0.9),transparent_40%),radial-gradient(circle_at_20%_0%,rgba(125,211,252,0.45),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(244,114,182,0.35),transparent_45%),radial-gradient(circle_at_50%_100%,rgba(167,139,250,0.28),transparent_45%)]
                bg-[#F2F4FF]"
            >
                {/* ── Header (original layout + customer picker added before Hide info) ── */}
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-2 pb-4">
                    <div className="flex items-center gap-3">
                        {/* Mobile sidebar toggle */}
                        {selectedCustomer && (
                            <button
                                type="button"
                                onClick={() => setMobileSidebarOpen((p) => !p)}
                                className="lg:hidden p-2 rounded-xl bg-white/60 border border-white/70 backdrop-blur
                                shadow-sm text-slate-600 hover:bg-white/80"
                                aria-label="Toggle sessions"
                            >
                                {mobileSidebarOpen ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        )}
                        <div>
                            <h1 className="lg:text-2xl text-xl font-semibold capitalize text-slate-900 flex">
                                {t('aura title')}
                            </h1>
                            <p className="text-slate-500 text-sm">
                                {t('aura tagline')}
                            </p>
                        </div>
                    </div>

                    <div className="text-[11px] text-slate-600">
                        {/* ── Customer picker dropdown ── */}
                        <div className="relative" ref={pickerRef}>
                            <button
                                type="button"
                                onClick={openPicker}
                                className={`w-full lg:w-auto rounded-full px-4 py-1.5 border backdrop-blur flex items-center justify-center lg:justify-between gap-1.5
                                shadow-[0_12px_30px_rgba(15,23,42,0.08)] hover:bg-white/75
                                ${selectedCustomer
                                    ? "border-white/70 bg-white/60 text-slate-700"
                                    : "border-indigo-300 bg-indigo-50 text-indigo-700 font-medium"
                                }`}
                            >
                                {selectedCustomer ? selectedCustomer.name : t('aura select customer')}
                                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {pickerOpen && (
                                <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-xl
                                shadow-2xl border border-gray-100 z-50 flex flex-col max-h-80">
                                    <div className="p-2 border-b">
                                        <input
                                            type="text"
                                            value={pickerSearch}
                                            onChange={handlePickerSearch}
                                            placeholder={t('aura search customers')}
                                            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs
                                            focus:outline-none focus:border-gray-400"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="flex-1 overflow-y-auto">
                                        {pickerLoading && pickerCustomers.length === 0 ? (
                                            <div className="p-4 text-center text-xs text-gray-400">{t('aura loading')}</div>
                                        ) : pickerCustomers.length === 0 ? (
                                            <div className="p-4 text-center text-xs text-gray-400">{t('aura no customers')}</div>
                                        ) : (
                                            <ul className="divide-y divide-gray-100">
                                                {pickerCustomers.map((c) => (
                                                    <li
                                                        key={c.id}
                                                        onClick={() => applyCustomer(c)}
                                                        className={`px-3 py-2.5 cursor-pointer hover:bg-gray-50 flex flex-col
                                                        ${selectedCustomer?.id === c.id ? "bg-indigo-50" : ""}`}
                                                    >
                                                        <span className="font-medium text-xs">{c.name || "—"}</span>
                                                        {c.email && (
                                                            <span className="text-[10px] text-gray-400 mt-0.5">{c.email}</span>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {pickerHasNext && !pickerLoading && (
                                            <div className="p-2 text-center border-t">
                                                <button
                                                    onClick={() => fetchPickerCustomers(pickerSearch, pickerPage + 1, true)}
                                                    className="text-xs text-blue-600 hover:underline"
                                                >
                                                    {t('aura load more')}
                                                </button>
                                            </div>
                                        )}
                                        {pickerLoading && pickerCustomers.length > 0 && (
                                            <div className="p-2 text-center text-xs text-gray-400">{t('aura loading')}</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Main content: only renders after customer is selected ── */}
                {!selectedCustomer ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-4">
                        <div className="rounded-[28px] border border-white/70 bg-white/55 backdrop-blur-xl
                        shadow-[0_26px_90px_rgba(79,70,229,0.2)] px-10 py-12 flex flex-col items-center gap-4 max-w-sm text-center">
                            <div className="w-14 h-14 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                                <img src="/assets/images/aura-icon.png" alt="Aura AI" className="w-8 h-8" />
                            </div>
                            <h2 className="text-base font-semibold text-slate-800">{t('aura no customer heading')}</h2>
                            <p className="text-sm text-slate-500">
                                {t('aura no customer hint')}
                            </p>
                            <button
                                type="button"
                                onClick={openPicker}
                                className="mt-2 rounded-full px-6 py-2 bg-indigo-600 text-white text-xs font-medium
                                hover:bg-indigo-700 shadow-md"
                            >
                                {t('aura select customer')}
                            </button>
                        </div>
                    </div>
                ) : isChatsLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <LoaderIcon />
                    </div>
                ) : (
                    <div className="relative flex-1 min-h-0">
                        {/* Mobile sidebar — rendered at page level, outside glass card so fixed works cleanly */}
                        {!isLarge && (
                            <>
                                {mobileSidebarOpen && (
                                    <div
                                        className="fixed inset-0 bg-black/30 z-[55]"
                                        onClick={() => setMobileSidebarOpen(false)}
                                    />
                                )}
                                <ChatSidebar
                                    query={query}
                                    setQuery={setQuery}
                                    activeFilter={activeFilter}
                                    setActiveFilter={setActiveFilter}
                                    isLoadingChats={isLoadingChats}
                                    visiblePinned={visiblePinned}
                                    recentChats={recentChats}
                                    activeChat={activeChat}
                                    menuChatId={menuChatId}
                                    sessionHasNext={sessionHasNext}
                                    mobileSidebarOpen={mobileSidebarOpen}
                                    isMobile={true}
                                    onCloseMobile={() => setMobileSidebarOpen(false)}
                                    onNewChat={handleNewChat}
                                    onSelectChat={handleSelectChat}
                                    onToggleMenu={(id) => setMenuChatId((prev) => prev === id ? "" : id)}
                                    onTogglePin={(id) => { togglePin(id); setMenuChatId(""); }}
                                    onRequestDelete={(id) => { setConfirmDeleteId(id); setMenuChatId(""); }}
                                    onLoadMore={() => fetchSessions(sessionPage + 1, true, customerCode, salespersonId || FALLBACK_SALESPERSON_ID, query, activeFilter)}
                                    setIsResizing={setIsResizing}
                                />
                            </>
                        )}

                        <div className="absolute -inset-1 rounded-[30px] bg-[radial-gradient(circle_at_20%_20%,rgba(125,211,252,0.35),transparent_40%),radial-gradient(circle_at_80%_20%,rgba(244,114,182,0.30),transparent_40%),radial-gradient(circle_at_50%_100%,rgba(167,139,250,0.25),transparent_45%)] blur-2xl" />
                        <div className="relative rounded-[28px] border border-white/70 bg-white/55 backdrop-blur-xl
                        shadow-[0_26px_90px_rgba(79,70,229,0.2)] overflow-hidden flex-1 min-h-0 h-full">
                            <div
                                className="grid h-full min-h-0"
                                style={{ gridTemplateColumns: isLarge ? `${sidebarWidth}px 1fr` : "1fr" }}
                            >
                                {/* Desktop sidebar — inside grid, static position */}
                                {isLarge && (
                                    <ChatSidebar
                                        query={query}
                                        setQuery={setQuery}
                                        activeFilter={activeFilter}
                                        setActiveFilter={setActiveFilter}
                                        isLoadingChats={isLoadingChats}
                                        visiblePinned={visiblePinned}
                                        recentChats={recentChats}
                                        activeChat={activeChat}
                                        menuChatId={menuChatId}
                                        sessionHasNext={sessionHasNext}
                                        mobileSidebarOpen={true}
                                        isMobile={false}
                                        onNewChat={handleNewChat}
                                        onSelectChat={handleSelectChat}
                                        onToggleMenu={(id) => setMenuChatId((prev) => prev === id ? "" : id)}
                                        onTogglePin={(id) => { togglePin(id); setMenuChatId(""); }}
                                        onRequestDelete={(id) => { setConfirmDeleteId(id); setMenuChatId(""); }}
                                        onLoadMore={() => fetchSessions(sessionPage + 1, true, customerCode, salespersonId || FALLBACK_SALESPERSON_ID, query, activeFilter)}
                                        setIsResizing={setIsResizing}
                                    />
                                )}

                                <section className="flex flex-col h-full min-h-0">
                                    <ChatTopBar
                                        activeChat={activeChat}
                                        renamingChatId={renamingChatId}
                                        renameValue={renameValue}
                                        setRenameValue={setRenameValue}
                                        onRenameStart={handleRenameStart}
                                        onRenameCommit={handleRenameCommit}
                                        customerDisplay={customerDisplay}
                                        salespersonDisplay={salespersonDisplay}
                                        chips={chips}
                                        onChipClick={handleChipClick}
                                    />

                                    <MessageList
                                        viewDensity={viewDensity}
                                        isLoadingHistory={isLoadingHistory}
                                        groupedMessages={groupedMessages}
                                        isSending={isSending}
                                        thinkingStage={thinkingStage}
                                        copyNoticeId={copyNoticeId}
                                        messagesEndRef={messagesEndRef}
                                        onCopy={handleCopy}
                                        setCopyNoticeId={setCopyNoticeId}
                                        onUpdateMessage={updateMessage}
                                        onStartEdit={startEditMessage}
                                        onReact={handleReact}
                                        formatTimestamp={formatTimestamp}
                                    />

                                    {/* No-customer warning (only reachable via keyboard shortcut) */}
                                    {showNoCustomerWarning && (
                                        <div className="mx-3 mb-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200
                                        text-amber-700 text-xs text-center">
                                            {t('aura no customer warning')}
                                        </div>
                                    )}

                                    <ChatComposer
                                        draft={draft}
                                        setDraft={setDraft}
                                        isSending={isSending}
                                        onSend={handleSend}
                                    />
                                </section>

                                {/* InfoPanel hidden temporarily */}
                            </div>
                        </div>
                    </div>
                )}

                <DeleteConfirmModal
                    confirmDeleteId={confirmDeleteId}
                    onCancel={() => setConfirmDeleteId("")}
                    onConfirm={() => { deleteChat(confirmDeleteId); setConfirmDeleteId(""); }}
                />

                {feedbackTarget && (
                    <FeedbackModal
                        target={feedbackTarget}
                        onClose={() => setFeedbackTarget(null)}
                        onSubmitted={handleFeedbackSubmitted}
                    />
                )}
            </div>
        </Layout>
    );
};

export default AiChatPage;

